// ── CAPR S5 — the bulk REVIEW actions: a review cadence, and a scheduled re-assessment ───────────────
//
// ⚠ EACH CONTROL IS THE SINGLE ACTION, ONCE, AND EVERY OUTCOME IS NAMED. The server applies the single
//   override / the single start per control, each in its own transaction with its own audit row, and
//   answers per control: changed / unchanged / started, skipped (with the reason) and failed. This bar
//   shows exactly that — nothing reports a count without the controls behind it.
// ⚠ THE DUE-BY IS THE SINGLE START'S. Each control's due date is computed by dueAtForReview — the Start
//   form's own calculation over the row's next_review_date and review_label (the resolver's facts) — and
//   the server sets the reason (scheduled_review). Nothing here does date arithmetic of its own.
// ⚠ ALWAYS VISIBLE, DISABLED AT ZERO, like the people bar; the server is the authority on who may act.

import { useMemo, useState } from 'react';
import { CTS_REVIEW_CADENCES, CTS_REVIEW_CADENCE_LABEL, type CtsReviewCadence } from '@grc/shared';
import { cn } from '@/lib/utils';
import type { TeamMember } from '@/api/types/settings.types';
import type { WorkspaceControl } from '@/api/types/cts-workspace.types';
import { useBulkSetReviewCadence } from '@/api/hooks/useCtsReview';
import { useBulkStartReassessment } from '@/api/hooks/useCtsTasks';
import { PeoplePicker } from './PeopleLanes';
import { dueAtForReview, FIRST_ASSESSMENT_DUE_DAYS } from './assessment/StartAssessmentForm';

const BTN = 'rounded-[7px] px-[11px] py-[6px] text-[12px] transition-colors disabled:opacity-40';
const FIELD = 'rounded-[7px] border border-border-subtle bg-bg-surface px-[9px] py-[6px] text-[12.5px] text-text-primary';

/** Why a control was not changed or started — the server's reason, in words. */
export const BULK_REVIEW_SKIP_TEXT: Record<string, string> = {
  not_authorized: 'you are not permitted to change it',
  duplicate: 'listed twice',
  invalid_cadence: 'not a review cadence',
  rationale_required: 'a rationale is required',
  rationale_too_long: 'the rationale is too long',
  not_in_workspace: 'not in the workspace',
  already_assigned: 'it already has open work',
  no_live_control: 'it is not a live control',
  invalid_due_at: 'the due date is not valid',
  failed: 'it could not be saved',
};

type Report =
  | { kind: 'cadence'; changed: string[]; unchanged: string[]; skipped: { reference: string; reason: string }[]; failed: { reference: string; reason: string }[] }
  | { kind: 'start'; started: string[]; skipped: { reference: string; reason: string }[]; failed: { reference: string; reason: string }[] };

export function BulkReviewBar({
  selected, rows, team, onClear,
}: {
  /** References of the VISIBLE, selected rows — never a filter-hidden one. */
  selected: string[];
  /** The visible rows: each one's review facts give its due-by. */
  rows: WorkspaceControl[];
  team: TeamMember[] | undefined;
  onClear: () => void;
}) {
  const [panel, setPanel] = useState<'cadence' | 'start' | null>(null);
  const [cadence, setCadence] = useState<CtsReviewCadence | 'default'>('default');
  const [rationale, setRationale] = useState('');
  const [assignee, setAssignee] = useState('');
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bulkCadence = useBulkSetReviewCadence();
  const bulkStart = useBulkStartReassessment();
  const busy = bulkCadence.isPending || bulkStart.isPending;
  const n = selected.length;
  const byRef = useMemo(() => new Map(rows.map((r) => [r.control_reference, r])), [rows]);
  const code = (reference: string) => byRef.get(reference)?.display_code ?? reference;
  const close = () => { setPanel(null); setRationale(''); setAssignee(''); setCadence('default'); setError(null); };

  const runCadence = () => {
    const value = cadence === 'default' ? null : cadence;
    bulkCadence.mutate(
      { items: selected.map((controlReference) => (value === null
        ? { controlReference, cadence: null }
        : { controlReference, cadence: value, rationale })) },
      {
        onSuccess: (d) => { setReport({ kind: 'cadence', changed: d.changed, unchanged: d.unchanged, skipped: d.skipped, failed: d.failed }); close(); onClear(); },
        onError: () => setError('The review cadences could not be changed. Nothing was changed.'),
      });
  };

  const runStart = () => {
    const now = new Date();
    bulkStart.mutate(
      {
        assigneeUserId: assignee,
        // ⚠ the single start's due-by, per control, from that control's own review facts
        items: selected.map((controlReference) => {
          const r = byRef.get(controlReference);
          return { controlReference, dueAt: r ? dueAtForReview(r, now) : null };
        }),
      },
      {
        onSuccess: (d) => { setReport({ kind: 'start', started: d.started.map((s) => s.reference), skipped: d.skipped, failed: d.failed }); close(); onClear(); },
        onError: () => setError('The re-assessments could not be started. Nothing was started.'),
      });
  };

  const needsRationale = cadence !== 'default';
  const cadenceReady = !needsRationale || rationale.trim() !== '';

  return (
    <div className="mb-[10px] flex flex-col gap-[8px]" data-testid="bulk-review-bar">
      <div role="region" aria-label="Bulk review actions"
        className={cn('flex flex-wrap items-center gap-[10px] rounded-[9px] border px-[14px] py-[10px]',
          n > 0 ? 'border-accent-cyan/35 bg-accent-cyan/8' : 'border-border-subtle bg-bg-surface-2')}>
        <span className={cn('text-[12.5px] font-medium', n > 0 ? 'text-text-primary' : 'text-text-faint')}>Review</span>
        <span className="flex flex-wrap items-center gap-[8px]">
          <button type="button" disabled={n === 0 || busy} onClick={() => { setReport(null); setPanel('cadence'); }}
            className={cn(BTN, 'border border-border-subtle bg-bg-surface text-text-primary')}>
            Set review cadence
          </button>
          <button type="button" disabled={n === 0 || busy} onClick={() => { setReport(null); setPanel('start'); }}
            className={cn(BTN, 'border border-border-subtle bg-bg-surface text-text-primary')}>
            Start re-assessment
          </button>
        </span>
      </div>

      {panel === 'cadence' && (
        <div className="flex flex-col gap-[8px] rounded-[9px] border border-accent-cyan/35 bg-bg-surface-2 p-[14px]" data-testid="bulk-cadence-panel">
          <p className="text-[13px] font-medium text-text-primary">
            Set the review cadence on {n} {n === 1 ? 'control' : 'controls'}
          </p>
          <label className="text-[11.5px] text-text-secondary" htmlFor="bulk-cadence">Review cadence</label>
          <select id="bulk-cadence" className={FIELD} value={cadence} onChange={(e) => setCadence(e.target.value as CtsReviewCadence | 'default')}>
            <option value="default">Use the organization default</option>
            {CTS_REVIEW_CADENCES.map((c) => <option key={c} value={c}>{CTS_REVIEW_CADENCE_LABEL[c]}</option>)}
          </select>
          {needsRationale && (
            <>
              <label className="text-[11.5px] text-text-secondary" htmlFor="bulk-cadence-rationale">Why these controls are reviewed on this cadence</label>
              <textarea id="bulk-cadence-rationale" rows={2} className={FIELD} value={rationale} onChange={(e) => setRationale(e.target.value)} />
            </>
          )}
          {error && <p className="text-[12px] text-cert-warn-fg" role="alert">{error}</p>}
          <span className="flex gap-[8px]">
            <button type="button" data-testid="bulk-cadence-confirm" disabled={!cadenceReady || busy} onClick={runCadence}
              className={cn(BTN, 'border border-accent-cyan/50 bg-bg-surface text-text-primary')}>Apply</button>
            <button type="button" disabled={busy} onClick={close} className={cn(BTN, 'text-text-secondary')}>Cancel</button>
          </span>
        </div>
      )}

      {panel === 'start' && (
        <div className="flex flex-col gap-[8px] rounded-[9px] border border-accent-cyan/35 bg-bg-surface-2 p-[14px]" data-testid="bulk-start-panel">
          <p className="text-[13px] font-medium text-text-primary">
            Start a scheduled re-assessment on {n} {n === 1 ? 'control' : 'controls'}
          </p>
          <label className="text-[11.5px] text-text-secondary" htmlFor="bulk-start-assignee">Assessor</label>
          <PeoplePicker id="bulk-start-assignee" lane="assignee" team={team} value={assignee} onChange={setAssignee} />
          <p className="text-[11px] text-text-faint" data-testid="bulk-start-due-note">
            Each control is due on its next review date, or {FIRST_ASSESSMENT_DUE_DAYS} days from today when it is overdue,
            not yet assessed or has no scheduled review — the same default as starting one control.
          </p>
          {error && <p className="text-[12px] text-cert-warn-fg" role="alert">{error}</p>}
          <span className="flex gap-[8px]">
            <button type="button" data-testid="bulk-start-confirm" disabled={assignee === '' || busy} onClick={runStart}
              className={cn(BTN, 'border border-accent-cyan/50 bg-bg-surface text-text-primary')}>Start</button>
            <button type="button" disabled={busy} onClick={close} className={cn(BTN, 'text-text-secondary')}>Cancel</button>
          </span>
        </div>
      )}

      {report && (
        <div className="rounded-[9px] border border-border-subtle bg-bg-surface-2 p-[12px] text-[12px] text-text-secondary" data-testid="bulk-review-report" role="status">
          {report.kind === 'cadence' ? (
            <>
              <p>{report.changed.length} {report.changed.length === 1 ? 'control' : 'controls'} changed
                {report.unchanged.length > 0 && ` · ${report.unchanged.length} already on that cadence`}.</p>
            </>
          ) : (
            <p>Re-assessment started on {report.started.length} {report.started.length === 1 ? 'control' : 'controls'}.</p>
          )}
          {[...report.skipped, ...report.failed].length > 0 && (
            <ul className="mt-[6px] list-disc pl-[18px]" data-testid="bulk-review-not-done">
              {[...report.skipped, ...report.failed].map((s, i) => (
                <li key={`${s.reference}-${i}`}>{code(s.reference)} — {BULK_REVIEW_SKIP_TEXT[s.reason] ?? s.reason}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
